import { useEffect, useState } from 'react';
import styled, { ThemeProvider } from 'styled-components';

interface Props {
  className?: string;
  labelledBy?: string;

  // If true, the toggle is disabled. If false, the toggle is enabled
  disabled?: boolean;
  theme?: string;

  // Callback function to invoke when the user clicks on the toggle.
  onChange?: (checked: boolean) => void;

  // If true, the toggle is checked. If false, the toggle is unchecked.
  // Use this if you want to treat the toggle as a controlled component
  active?: boolean;
  noMargin?: boolean;
  noPadding?: boolean;
  verticalAlignTop?: boolean;

  // If true on initial render, the toggle is checked.
  // If false on initial render, the toggle is unchecked.
  // Use this if you want to treat the toggle as an uncontrolled component
  defaultActive?: boolean;
}

/** @deprecated prefer `import { Switch } from '@workramp/ui';` */
export default function Toggle({
  className,
  disabled,
  theme,
  onChange,
  active,
  noMargin,
  noPadding,
  verticalAlignTop,
  defaultActive,
  labelledBy,
}: Props) {
  const [isActive, setIsActive] = useState(!!(active || defaultActive));
  const [focus, setFocus] = useState(false);
  const themeToUse = themes[theme] || themes.default;

  useEffect(() => {
    setIsActive(!!(active || defaultActive));
  }, [active, defaultActive]);

  const handleChange = ({ target }) => {
    // Only set active state if being used as an uncontrolled component
    if (defaultActive != null) setIsActive(target.checked);
    if (onChange) {
      onChange(target.checked);
    }
  };

  return (
    <ThemeProvider theme={themeToUse}>
      <ToggleTrackWrapper className={className} $noMargin={noMargin} $noPadding={noPadding}>
        <ToggleTrack
          $verticalAlignTop={verticalAlignTop}
          tabIndex={0}
          onKeyDown={(event) => {
            const { key } = event;
            if (key === 'Enter' || key === ' ') handleChange(event);
          }}
        >
          <HiddenInput
            type="checkbox"
            onChange={handleChange}
            checked={isActive}
            disabled={!!disabled}
            aria-labelledby={labelledBy}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
          />
          <Thumb checked={isActive} focus={focus} />
        </ToggleTrack>
      </ToggleTrackWrapper>
    </ThemeProvider>
  );
}

const themes = {
  default: {
    toggleTrackWrapper: {
      margin: '4px 2px',
      padding: '0 0.5em',
    },
    toggleTrack: {
      width: '32px',
      height: '12px',
      verticalAlign: 'middle',
    },
    thumb: {
      border: null,
      top: '0px',
      left: '2px',
      bottom: '0',
      right: '2px',
      backgroundColor: '#ccc',
      before: {
        height: '16px',
        width: '16px',
        top: '-2px',
        left: '0',
        bottom: '0',
        backgroundColor: '#aaa',
        border: null,
        checked: {
          translateX: '12px',
          backgroundColor: '#1F589A',
        },
      },
      checked: {
        backgroundColor: '#2196F3',
      },
      focus: {
        boxShadow: '0 0 1px #2196F3',
      },
    },
  },
  toolbar: {
    toggleTrackWrapper: {
      margin: '4px 2px',
      padding: '0',
    },
    toggleTrack: {
      marginRight: '.5em',
      width: '32px',
      height: '15px',
      verticalAlign: 'middle',
    },
    thumb: {
      border: null,
      top: '0',
      left: '2px',
      right: '2px',
      bottom: '0',
      backgroundColor: '#c5d1de',
      before: {
        height: '13px',
        width: '13px',
        left: '1px',
        bottom: '0px',
        top: '1px',
        backgroundColor: '#fff',
        checked: {
          translateX: '13px',
          backgroundColor: '#fff',
        },
      },
      checked: {
        backgroundColor: '#2196F3',
      },
      focus: {
        boxShadow: '0 0 1px #2196F3',
      },
    },
  },
  v2: {
    toggleTrackWrapper: {
      margin: '4px 2px',
      padding: '0 0.5em',
    },
    toggleTrack: {
      width: '24px',
      height: '16px',
      verticalAlign: 'middle',
    },
    thumb: {
      border: '1px solid #E6EAEF',
      top: '0px',
      left: '-2px',
      bottom: '0',
      right: '2px',
      backgroundColor: '#E6EAEF',
      before: {
        height: '16px',
        width: '16px',
        top: '-1px',
        left: '-1px',
        bottom: '0',
        backgroundColor: '#FFFFFF',
        border: '1px solid #E6EAEF',
        checked: {
          border: '1px solid #0F48E6',
          translateX: '8px',
          backgroundColor: '#FFFFFF',
        },
      },
      checked: {
        border: '1px solid #0F48E6',
        backgroundColor: '#0F48E6',
      },
      focus: {
        boxShadow: '0 0 1px #2196F3',
      },
    },
  },
};

const ToggleTrackWrapper = styled.div<{ $noMargin?: boolean; $noPadding?: boolean; theme: any }>`
  text-align: center;
  font-size: 12px;
  padding: ${({ theme, $noPadding }) => ($noPadding ? null : theme.toggleTrackWrapper.padding)};
  display: inline-block;
  margin: ${({ theme, $noMargin }) => ($noMargin ? null : theme.toggleTrackWrapper.margin)};
`;

const ToggleTrack = styled.label<{
  $verticalAlignTop?: boolean;
  theme: any;
}>`
  position: relative;
  display: inline-block;
  vertical-align: ${({ theme, $verticalAlignTop }) => ($verticalAlignTop ? 'top' : theme.toggleTrack.verticalAlign)};
  width: ${({ theme }) => theme.toggleTrack.width};
  height: ${({ theme }) => theme.toggleTrack.height};
`;

const HiddenInput = styled.input`
  display: none;
`;

const Thumb = styled.span<{
  checked: boolean;
  focus: boolean;
  theme: any;
}>`
  position: absolute;
  cursor: pointer;
  box-sizing: border-box;

  top: ${({ theme }) => theme.thumb.top};
  left: ${({ theme }) => theme.thumb.left};
  right: ${({ theme }) => theme.thumb.right};
  bottom: ${({ theme }) => theme.thumb.bottom};

  background-color: ${({ theme }) => theme.thumb.backgroundColor};
  border-radius: 34px;
  border: ${({ theme, checked }) => (checked ? theme.thumb.checked.border : theme.thumb.border)};

  -webkit-transition: 0.3s;
  transition: 0.3s;

  &:before {
    position: absolute;
    box-sizing: border-box;
    content: '';
    height: ${({ theme }) => theme.thumb.before.height};
    width: ${({ theme }) => theme.thumb.before.width};

    top: ${({ theme }) => theme.thumb.before.top};
    left: ${({ theme }) => theme.thumb.before.left};
    bottom: ${({ theme }) => theme.thumb.before.bottom};

    background-color: ${({ theme }) => theme.thumb.before.backgroundColor};
    border-radius: 50%;
    border: ${({ theme }) => theme.thumb.before.border};

    -webkit-transition: 0.3s;
    transition: 0.3s;

    ${({ checked, theme }) =>
      checked &&
      `
     -webkit-transform: translateX(${theme.thumb.before.checked.translateX});
      -ms-transform: translateX(${theme.thumb.before.checked.translateX});
      transform: translateX(${theme.thumb.before.checked.translateX});
      background-color: ${theme.thumb.before.checked.backgroundColor};
      border: ${theme.thumb.before.checked.border};
    `};
  }

  ${({ checked, theme }) =>
    checked &&
    `
    background-color: ${theme.thumb.checked.backgroundColor};
  `}

  ${({ focus, theme }) =>
    focus &&
    `
    box-shadow: ${theme.thumb.focus.boxShadow};
  `}
`;
